import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import moment from 'moment';
import { reduxForm, SubmissionError } from 'redux-form';

import { withStyles } from '@material-ui/core/styles';
import RootRef from '@material-ui/core/RootRef';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import dateFormat from 'config/dateFormat';

import { updateOrderLines } from 'redux/modules/ordersLines';
import { addNotification } from 'redux/modules/notifications';

import objectDiff from 'utils/objectDiff';

import OrdersTableHead from './OrdersTableHead';
import OrdersTableEmpty from './OrdersTableEmpty';
import OrdersTableRow from './OrdersTableRow';

const styles = theme => ({
  root: {
    position: 'relative',
    width: '100%',
    height: 'calc(100vh - 253px)',
    overflowX: 'auto',
    overflowY: 'visible',
    overscrollBehavior: 'contain',
    transition: theme.transitions.create(['height'], {
      duration: theme.transitions.duration.short,
    }),
    [theme.breakpoints.down('sm')]: {
      height: 'calc(100vh - 140px)',     
    },
  },
  rootMobileFilterExpanded: {
    [theme.breakpoints.down('sm')]: {
      height: 'calc(100vh - 274px)',
    },
  },
  table: {
    minWidth: 1300,
    tableLayout: 'fixed',
    '& thead': {
      position: 'sticky',
      zIndex: 20,
      top: 0,
    },
    '& thead th:first-child': {
      borderTopLeftRadius: 10,
    },
    '& thead th:last-child': {
      borderTopRightRadius: 10,
    },
    '& thead th': {
      position: 'relative',
      zIndex: 1,
    },
  },
  typography: {
    paddingLeft: 10,
    paddingBottom: 8,
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
});

function onSubmit(values, dispatch, props) {
  const normalizeValues = (formValues, key) => {
    const normalizedValues = Object.assign(
      {},
      formValues,
      {
        ...formValues.Reimbs ? { Reimbs: Number(formValues.Reimbs.replace(/[^0-9.]/g, '')) } : {},
        ...formValues['Driver ID'] === '' && { 'Driver ID': null },
        ...formValues.orderLineInsuranceParam ? {
          orderLineInsuranceParam: Object.keys(formValues.orderLineInsuranceParam)
            .reduce((accumulator, currentValue) => ({
              ...accumulator,
              [currentValue]: {
                ...formValues.orderLineInsuranceParam[currentValue].Reimbs
                  ? { Reimbs: Number(formValues.orderLineInsuranceParam[currentValue].Reimbs.replace(/[^0-9.]/g, '')) } : {},
                ...formValues.orderLineInsuranceParam[currentValue].Reimbs === ''
                  ? { Reimbs: null } : {},
                ...formValues.orderLineInsuranceParam[currentValue]['Reimbs Date']
                  ? { 'Reimbs Date': moment(formValues.orderLineInsuranceParam[currentValue]['Reimbs Date'], dateFormat).format(dateFormat) } : {},
                ...formValues.orderLineInsuranceParam[currentValue]['Reimbs Date'] === ''
                  ? { 'Reimbs Date': null } : {},
              },
            }), {}),
        } : {},
        ...formValues.Status && formValues.Status.length > 0 ? {
        'Workflow Status ID': formValues.Status
          .map(statusItem => statusItem.value),
        } : {},
        ...formValues.Status && formValues.Status.filter(statusItem => statusItem.nextWorkflowStatusID).length > 0 ? {
          'Next Workflow Status ID': formValues.Status
            .map(statusItem => statusItem.nextWorkflowStatusID || statusItem.value)
        } : {},
        ...!formValues['Is Delivered']
          && formValues.Status
          && formValues.Status.filter(statusItem => statusItem.isDelivered).length > 0 ? {
          'Is Delivered': true,
        } : {},
        ...formValues['Date of Delivery'] || formValues['Time of Delivery'] ? {
          'Date of Delivery': `${values[key]['Time of Delivery']} ${values[key]['Date of Delivery']}`,
        } : {},
      },
    );

    if (normalizedValues.Reimbs === '') {
      normalizedValues.Reimbs = null;
    }

    if (normalizedValues['Reimbs Date'] !== undefined) {
      if (normalizedValues['Reimbs Date'] === '') {
        normalizedValues['Reimbs Date'] = null;
      } else {
        normalizedValues['Reimbs Date'] =
            moment(normalizedValues['Reimbs Date'], dateFormat).format(dateFormat);
      }
    }

    delete normalizedValues.selected;
    delete normalizedValues['Assigned User Name'];
    delete normalizedValues['Selected Assigned User Name'];
    delete normalizedValues['Driver Name'];
    delete normalizedValues['Selected Driver Name'];
    delete normalizedValues.Status;
    delete normalizedValues['Time of Delivery'];

    return normalizedValues;
  };

  if (props.pristine) {
    throw new SubmissionError({
      _error: 'Nothing to submit',
    });
  }
  const diffValues = objectDiff(values, props.initialValues);
  return new Promise((resolve, reject) => {
    dispatch(updateOrderLines({
      values: Object.keys(diffValues).map((key) => {
        const currentDiffValues = diffValues[key];
        if (currentDiffValues.Status) {
          currentDiffValues.Status = values[key].Status;
        }
        const normalizedValues = normalizeValues(currentDiffValues, key);
        if (normalizedValues.orderLineInsuranceParam) {
          normalizedValues.orderLineInsuranceParam =
          Object.keys(normalizedValues.orderLineInsuranceParam)
            .reduce((accumulator, currentValue) => ({
              ...accumulator,
              [currentValue]: {
                ...normalizedValues.orderLineInsuranceParam[currentValue],
                ...values[key].orderLineInsuranceParam[currentValue].ID
                  ? { ID: values[key].orderLineInsuranceParam[currentValue].ID } : {},
                ...normalizedValues.orderLineInsuranceParam[currentValue]['Reimbs Date'] !== undefined
                  && values[key].orderLineInsuranceParam[currentValue].Reimbs
                  ? { Reimbs: Number(values[key].orderLineInsuranceParam[currentValue].Reimbs.replace(/[^0-9.]/g, '')) } : {},
              },
            }), {});
        }
        if (normalizedValues['Check Book Sheet']) {
          normalizedValues['Check Book Sheet'] =
          Object.keys(normalizedValues['Check Book Sheet'])
            .reduce((accumulator, currentValue) => ({
              ...accumulator,
              [currentValue]: {
                ...normalizedValues['Check Book Sheet'][currentValue],
                ...values[key]['Check Book Sheet'][currentValue],
              } }), {});
        }
        return {
          ID: key.replace('-orderLine', ''),
          orderID: values[key].orderID,
          ...normalizedValues,
        };
      }),
      resolve,
      reject,
      showNotification: Object.keys(values).length === 1,
    }));
  }).then(res => res,
    (err) => {
      console.log(err);
      if (err.error) {
        dispatch(addNotification({
          message: err.error,
          variant: 'warning',
        }));
        throw new SubmissionError({ _error: err.error });
      }
      dispatch(addNotification({
        message: err,
        variant: 'warning',
      }));
      throw new SubmissionError(err);
    });
}

function validateForm(values) {
  const errors = {};
  Object.keys(values)
    .filter(key => key.indexOf('-orderLine') !== -1)
    .forEach((orderLine) => {
      if (values[orderLine].Status.length === 0) {
        errors[orderLine] = errors[orderLine] || {};
        errors[orderLine]['Status Name'] = 'Please, select status from list';
      }

      if (!values[orderLine]['Assigned User ID']
        || values[orderLine]['Assigned User ID'].length === 0) {
        errors[orderLine] = errors[orderLine] || {};
        errors[orderLine]['Assigned User Name'] = 'Please, select user from list';
      }

      if (values[orderLine]['Driver Name']
        && (!values[orderLine]['Driver ID']
        || values[orderLine]['Driver ID'].length === 0)) {
        errors[orderLine] = errors[orderLine] || {};
        errors[orderLine]['Driver Name'] = 'Please, select driver from list';
      }

      if (values[orderLine].orderLineInsuranceParam) {
        const orderLineInsuranceParamErrors = Object.keys(values[orderLine].orderLineInsuranceParam)
          .reduce((accumulator, currentValue) => {
            if (values[orderLine].orderLineInsuranceParam[currentValue]['Reimbs Date']
            && !values[orderLine].orderLineInsuranceParam[currentValue].Reimbs) {
              return {
                ...accumulator,
                [currentValue]: {
                  'Reimbs Date': 'Reimbs field is required if Date is set',
                },
              };
            }
            return accumulator;
          }, {});
        if (Object.keys(orderLineInsuranceParamErrors).length > 0) {
          errors[orderLine] = errors[orderLine] || {};
          errors[orderLine].orderLineInsuranceParam = orderLineInsuranceParamErrors;
        }
      }
    });

  return errors;
}

const OrdersTable = ({
  items,
  count,
  variant,
  onFormTextFieldBlur,
  submitting,
  handleSubmit,
  change,
  sort,
  sortBy,
  onSortingChange,
  onStockLineCreate,
  loading,
  business,
  classes,
  mobileFilterExpanded,
  onScroll,
  scrollRef,
  style,
  initialValues,
}) => {
  return (
  <form
    noValidate
    autoComplete="off"
    onSubmit={handleSubmit}
  >
    {(loading === 0 || items.length > 0) && (
      <Typography className={classes.typography}>
        Orders ({count})
      </Typography>
    )}
    <RootRef rootRef={scrollRef}>
      <Paper
        style={style}
        className={classNames(classes.root, {
          [classes.rootMobileFilterExpanded]: mobileFilterExpanded,
        })}
        onScroll={onScroll}
      >
        {(loading === 0 && items.length === 0) &&
          <OrdersTableEmpty />
        }
        {items.length > 0 &&
          <Fragment>
            <Table className={classes.table}>
              <OrdersTableHead
                variant={variant}
                sort={sort}
                sortBy={sortBy}
                onSortingChange={onSortingChange}
                loading={loading}
              />
              <TableBody>
                {items.map((item, index) => (
                  <OrdersTableRow
                    key={item.ID}
                    variant={variant}
                    onTextFieldBlur={onFormTextFieldBlur}
                    submitting={submitting}
                    change={change}
                    onStockLineCreate={onStockLineCreate}
                    business={business}
                    notesTooltipPlacement="left"
                    last={index === items.length - 1}
                    {...item}
                  />
                ))}
              </TableBody>
            </Table>
          </Fragment>
        }
      </Paper>
    </RootRef>
  </form>
  );
};

OrdersTable.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  change: PropTypes.func.isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number.isRequired,
  })).isRequired,
  variant: PropTypes.oneOf([
    'workflow',
    'combined',
    'connected',
  ]).isRequired,
  onFormTextFieldBlur: PropTypes.func,
  submitting: PropTypes.bool.isRequired,
  sort: PropTypes.oneOf(['asc', 'desc']),
  sortBy: PropTypes.string,
  onSortingChange: PropTypes.func,
  onStockLineCreate: PropTypes.func,
  loading: PropTypes.number,
  business: PropTypes.shape({
    ID: PropTypes.number,
  }),
  mobileFilterExpanded: PropTypes.bool,
  onScroll: PropTypes.func,
  scrollRef: PropTypes.func,
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    table: PropTypes.string.isRequired,
  }).isRequired,
};

OrdersTable.defaultProps = {
  onFormTextFieldBlur: () => {},
  formNameStem: 'orderDetailsForm',
  sort: null,
  sortBy: null,
  onSortingChange: null,
  loading: 0,
  business: {},
  onStockLineCreate: () => {},
  mobileFilterExpanded: false,
  onScroll: undefined,
  scrollRef: undefined,
};

export default reduxForm({
  onSubmit,
  validate: validateForm,
  enableReinitialize: true,
  // keepDirtyOnReinitialize: true,
})(withStyles(styles)(OrdersTable));



// WEBPACK FOOTER //
// ./src/components/workflow/OrdersTable/OrdersTable.js